export const TOKEN_KEY = 'gefsan.token'

/**
 * Evento disparado quando a sessao expira de vez (401 sem refresh possivel).
 * O AuthContext escuta e faz o logout — o cliente HTTP nao conhece React, entao
 * avisa a UI por evento em vez de mexer no estado direto.
 */
export const EVENTO_SESSAO_EXPIRADA = 'gefsan:sessao-expirada'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    message: string,
    readonly errors?: Record<string, string[]>,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// Base da API real. Em dev, aponte VITE_API_URL para o proxy do Vite (`/api/v1`)
// se quiser evitar CORS; o default bate direto no backend local.
const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8080/api/v1'

// Rotas de autenticacao nao entram no ciclo de refresh: um 401 aqui e credencial
// invalida, nao token expirado — tentar refresh criaria loop.
const ROTAS_AUTH = ['/auth/login', '/auth/refresh', '/auth/registrar', '/auth/logout']

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY)
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function encerrarSessao(): void {
  localStorage.removeItem(TOKEN_KEY)
  window.dispatchEvent(new Event(EVENTO_SESSAO_EXPIRADA))
}

async function parse(res: Response): Promise<never> {
  let code: string | null = null
  let message = `Erro ${res.status}`
  let errors: Record<string, string[]> | undefined

  try {
    const body = (await res.json()) as { code?: string; message?: string; errors?: Record<string, string[]> }
    code = body.code ?? null
    message = body.message ?? message
    errors = body.errors
  } catch {
    /* resposta sem corpo JSON */
  }

  throw new ApiError(res.status, code, message, errors)
}

// Refresh single-flight: varias requisicoes que tomam 401 ao mesmo tempo dividem
// UMA chamada a /auth/refresh, em vez de dispararem uma cada.
let refreshEmAndamento: Promise<boolean> | null = null

function renovarToken(): Promise<boolean> {
  if (refreshEmAndamento) return refreshEmAndamento

  const p = (async (): Promise<boolean> => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        // permite refresh por cookie httpOnly, caso o backend emita o refresh assim
        credentials: 'include',
      })
      if (!res.ok) return false
      const body = (await res.json()) as { token?: string }
      if (!body.token) return false
      localStorage.setItem(TOKEN_KEY, body.token)
      return true
    } catch {
      return false
    }
  })()

  refreshEmAndamento = p
  void p.finally(() => {
    if (refreshEmAndamento === p) refreshEmAndamento = null
  })
  return p
}

function executar(path: string, init: RequestInit): Promise<Response> {
  return fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...init.headers,
    },
  })
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res = await executar(path, init)

  // 401: token expirado. Renova uma vez e repete a requisicao original; se o
  // refresh falhar (ou o retry ainda der 401), encerra a sessao e a UI vai ao login.
  if (res.status === 401 && !ROTAS_AUTH.some((p) => path.startsWith(p))) {
    if (await renovarToken()) {
      res = await executar(path, init)
      if (res.status === 401) encerrarSessao()
    } else {
      encerrarSessao()
    }
  }

  if (!res.ok) await parse(res)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const get = <T>(path: string) => api<T>(path)
export const post = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })
export const put = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'PUT', body: JSON.stringify(body) })
export const patch = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' })

/**
 * SSE para o refino de texto pela IA (GET /vistorias/{id}/relatorio/ia/stream).
 *
 * Nao usamos EventSource, embora a rota seja GET: ele nao aceita header
 * Authorization (so cookie), e o backend e stateless com JWT. Entao lemos o
 * ReadableStream do fetch e fazemos o parse dos frames `data:` na mao.
 */
export async function streamSSE(
  path: string,
  onDelta: (texto: string) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'GET',
    headers: { Accept: 'text/event-stream', ...authHeaders() },
    signal,
  })
  if (res.status === 401) encerrarSessao()
  if (!res.ok) await parse(res)
  if (!res.body) throw new ApiError(500, null, 'Resposta sem corpo')

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    // Frames sao separados por linha em branco; um frame pode chegar partido em
    // dois chunks, por isso o que sobra fica no buffer.
    const frames = buffer.split('\n\n')
    buffer = frames.pop() ?? ''

    for (const frame of frames) {
      for (const linha of frame.split('\n')) {
        if (!linha.startsWith('data:')) continue
        const dado = linha.slice(5).trim()
        if (dado === '[DONE]') return
        try {
          const { delta, erro } = JSON.parse(dado) as { delta?: string; erro?: string }
          if (erro) throw new ApiError(500, 'ia_indisponivel', erro)
          if (delta) onDelta(delta)
        } catch (e) {
          if (e instanceof ApiError) throw e
          // frame malformado: ignora, o stream continua
        }
      }
    }
  }
}
